<template>
  <div
    ref="navigation"
    class="navigation"
  >
    <span
      class="visually-hidden"
      v-text="$string('general.site_header.navigation')"
    />

    <nav class="navigation__nav">
      <ul class="navigation__list">
        <li
          v-for="(tier1) of desktop.items"
          :key="tier1.handle"
          class="navigation__item"
        >
          <button
            v-if="getHasChildren(tier1)"
            ref="tier1Links"
            class="navigation__link"
            :class="{
              [$class('active')]: getActiveState(tier1),
              [tier1.handle]: true
            }"
            :style="getLinkStyles(tier1.title)"
            type="button"
            @blur="handleLinkBlur(tier1)"
            @focus="handleLinkFocus(tier1)"
            @mouseenter="handleLinkHover(tier1)"
            @mouseleave="handleLinkLeave"
            @click="handleRedirect($localizeUrl(tier1))"
          >
            <h2
              class="navigation__link-title text-body-xs-regular"
              v-text="getLinkTitle(tier1.title)"
            />
          </button>

          <a
            v-else
            ref="tier1Links"
            class="navigation__link text-body-xs-regular"
            :href="$localizeUrl(tier1.url)"
            @focus="handleLinkFocus(tier1)"
            @blur="handleLinkBlur(tier1)"
          >
            <span
              class="navigation__link-title"
              v-text="getLinkTitle(tier1.title)"
            />
          </a>
        </li>
      </ul>
    </nav>

    <overlay
      key="navigation"
      ref="overlay"
      block-class="navigation-overlay"
      :class-container="overlayContainerClass"
      :close-on-backdrop-hover="closeBackdropOnHover"
      direction="right"
      namespace="navigation"
      :overlay-label="$string('general.site_header.navigation')"
      :show-close="false"
      :teleport="false"
      type="drawer"
    >
      <template #body>
        <div class="navigation__overlay-container">
          <div class="navigation__overlay-inner">
            <div class="navigation__tier1-container" />

            <div class="navigation__tier2-container">
              <template
                v-for="(tier1, tier1Index) of desktop.items"
                :key="`${tier1.handle}-tier2`"
              >
                <transition name="fade-up">
                  <ul
                    v-if="getHasChildren(tier1) && getActiveState(tier1)"
                    class="navigation__tier2 navigation__transition"
                  >
                    <li
                      v-for="(tier2, tier2Index) of tier1.items"
                      :key="tier2.handle"
                      class="navigation__menu-item"
                    >
                      <button
                        v-if="getHasChildren(tier2)"
                        ref="tier2Links"
                        class=" navigation__link navigation__button text-body-l-regular"
                        :class="{ [$class('active')]: getActiveState(tier2) }"
                        :style="getLinkStyles(tier2.title)"
                        type="button"
                        :aria-expanded="getActiveState(tier2)"
                        @blur="handleLinkBlur(tier2, tier1Index, tier2Index)"
                        @focus="handleLinkFocus(tier2)"
                        @click="handleRedirect($localizeUrl(tier2))"
                        @mouseenter="handleLinkHover(tier2)"
                        @mouseleave="handleLinkLeave"
                      >
                        <span
                          class="visually-hidden"
                          v-text="$string('accessibility.open')"
                        />

                        <span
                          class="navigation__link-title"
                          :class="getLinkSizeClass(tier2.title)"
                          v-text="getLinkTitle(tier2.title)"
                        />

                        <span
                          v-if="getLinkBadge(tier2.title)"
                          class="navigation__link-badge"
                          v-text="getLinkBadge(tier2.title)"
                        />
                      </button>

                      <a
                        v-else
                        ref="tier2Links"
                        class=" navigation__link navigation__link-title text-body-l-regular"
                        :href="$localizeUrl(tier2.url)"
                        :style="getLinkStyles(tier2.title)"
                        @blur="handleLinkBlur(tier2, tier1Index, tier2Index)"
                        @focus="handleLinkFocus(tier2)"
                        @mouseenter="handleLinkHover(tier2)"
                        @mouseleave="handleLinkLeave"
                      >
                        <span
                          class="navigation__link-title"
                          :class="getLinkSizeClass(tier2.title)"
                          v-text="getLinkTitle(tier2.title)"
                        />

                        <span
                          v-if="getLinkBadge(tier2.title)"
                          class="navigation__link-badge"
                          v-text="getLinkBadge(tier2.title)"
                        />
                      </a>
                    </li>
                    <li tabindex="0">
                      <!-- allow last link to 'blur' -->
                    </li>
                  </ul>
                </transition>
              </template>
            </div>

            <div
              ref="tier3"
              class="navigation__tier3-container"
              :class="{
                [$class('active')]: activeTier2,
                'not-previously-active': !previousActiveTier2,
              }"
            >
              <template
                v-for="(tier1, tier1Index) of desktop.items"
                :key="`${tier1.handle}-tier3`"
              >
                <template
                  v-for="(tier2, tier2Index) of tier1.items"
                  :key="`${tier2.handle}-tier3`"
                >
                  <transition name="fade-up">
                    <ul
                      v-if="getHasChildren(tier2) && getActiveState(tier2)"
                      class="navigation__tier3 navigation__transition"
                    >
                      <li
                        v-for="(tier3, tier3Index) of tier2.items"
                        :key="tier3.handle"
                        class="navigation__menu-item"
                      >
                        <a
                          ref="tier3Links"
                          class="navigation__link text-body-xs-regular"
                          :href="$localizeUrl(tier3.url)"
                          :style="getLinkStyles(tier3.title)"
                          @blur="handleLinkBlur(tier3, tier1Index, tier2Index, tier3Index)"
                        >
                          <span
                            class="navigation__link-title"
                            v-text="getLinkTitle(tier3.title)"
                          />
                        </a>
                      </li>
                    </ul>
                  </transition>
                </template>
              </template>
            </div>
          </div>
        </div>
      </template>
    </overlay>
  </div>
</template>

<script>

/**
 * Vue: Navigation (navigation)
 * -----------------------------------------------------------------------------
 * Desktop navigation links.
 * - No style import as it's included in critical stylesheet.
 *
 * @param {Object} desktop - Desktop linklist handle and items.
 *
 */
import { mapActions, mapGetters, mapState } from 'vuex'
import { klaviyoEvent } from '~/helpers/klaviyo'

import Overlay from '~global/overlay/overlay'

import { focusable } from '~/helpers/accessibility'

export default {
  // eslint-disable-next-line vue/multi-word-component-names
  name: 'Navigation',

  components: {
    Overlay,
  },

  props: {
    desktop: {
      type: Object,
      default: () => ({
        handle: 'main-menu',
        items: [],
      }),
    },
  },

  data() {
    return {
      activeTier1: false,
      activeTier2: false,
      previousActiveTier2: false,
      redirecting: false,
      timeout: false,
      closeBackdropOnHover: true,
      focusLeaving: false,
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getActiveOverlay: 'overlays/getActiveOverlay',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute overlay is active.
     */
    isActive() {
      return this.getActiveOverlay?.namespace === 'navigation'
    },

    /**
     * Compute overlay container class.
     * @returns {String}
     */
    overlayContainerClass() {
      return this.activeTier2
        ? this.$class('expanded')
        : ''
    },
  },

  watch: {

    /**
     * Watch active tier 2 updates to set previous state.
     * - Also update focus trap.
     * @param {Boolean|Object} newState - New tier 2 state.
     * @param {Boolean|Object} oldState - Previous tier 2 state.
     */
    async activeTier2(newState, oldState) {
      if (!newState) {
        this.previousActiveTier2 = false

        await this.$nextTick()
        this.$refs.overlay.update({ currentElement: document.activeElement })

        return
      }

      this.previousActiveTier2 = oldState

      /**
       * Set focus to tier 3 links after animation has finished.
       */
      await cnvs.Sleep(400)
      const tier3Links = focusable(this.$refs.tier3)

      const currentElement = tier3Links.length
        ? tier3Links[0]
        : document.activeElement

      this.$refs.overlay.update({ currentElement })
    },

    /**
     * Watch is active to lear links.
     */
    isActive() {
      if (this.isActive) {
        return
      }

      this.clearActiveLinks()
    },

    /**
     * Watch is mobile to close overlay.
     */
    isMobile() {
      if (!this.isMobile) {
        this.setNavigationWidth()
        return
      }

      this.clearActiveLinks()
      this.closeOverlay('navigation')
    },
  },

  async mounted() {
    this.$refs.overlay.addCallback(this.clearActiveLinks)

    await this.$nextTick()
    this.setNavigationWidth()
    await this.$nextTick()
    this.checkHoverState()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      closeOverlay: 'overlays/close',
      openOverlay: 'overlays/open',
    }),

    /**
     * check to determine if initial teir 1 link was clicked
     * and nav still maintains hover
     */
    checkHoverState() {
      if (this.isMobile) {
        return
      }

      setTimeout(() => {
        const tier1Link = JSON.parse(localStorage.getItem('tier1Link'))

        if (tier1Link) {
          if (!document.querySelector('.navigation:hover')) {
            localStorage.removeItem('tier1Link')
          }
        }
      }, 250)
    },

    /**
     * Sets the width of the navigation menu for desktop
     */
    setNavigationWidth() {
      const navComputedStyle = window.getComputedStyle(this.$refs.navigation)
      const navWidth = parseInt(navComputedStyle.width.replace('px', '')) + 150

      document.documentElement.style.setProperty(
        '--navigation-tier-width',
        `${navWidth}px`,
      )
    },

    /**
     * Get if link has children.
     * @param {Object} link - Tier link object.
     * @returns {Boolean}
     */
    getHasChildren(link) {
      return link.items?.length
    },

    /**
     * Get active state.
     * @param {Object} link - Tier link object.
     * @returns {Boolean}
     */
    getActiveState(link) {
      const activeTier = link.tier === 1
        ? this.activeTier1
        : this.activeTier2

      return activeTier.handle === link.handle
    },

    /**
     * Get badge string for link title.
     * @param {String} title - Title including link settings.
     * @returns {String|Null}
     */
    getLinkBadge(title) {
      const regex = new RegExp(/badge:(?<value>\w+)/, 'i')
      const badge = regex.exec(title)

      return badge && badge[1] ? badge[1].replace('_', ' ') : null
    },

    /**
     * Get sanitized link title.
     * @param {String} title - Title including link settings.
     * @returns {String}
     */
    getLinkTitle(title) {
      const regex = new RegExp(/\w+:\S+/)

      return title.split(' ').filter(word => {
        return !word.match(regex)
      }).join(' ')
    },

    /**
     * Get link styles.
     * @param {String} title - Title including link settings.
     * @returns {String}
     */
    getLinkStyles(title) {
      const linkColorRegex = new RegExp(/\scolor:(?<value>#?\w+)/, 'i')
      const linkColor = linkColorRegex.exec(title)
      const badgeColorRegex = new RegExp(/badgeColor:(?<value>#?\w+)/, 'i')
      const badgeColor = badgeColorRegex.exec(title)
      const gapRegex = new RegExp(/gap:(?<value>\d+)/, 'i')
      const gap = gapRegex.exec(title)
      const linkHoverColorRegex = new RegExp(/linkHoverColor:(?<value>#?\w+)/, 'i')
      const linkHoverColor = linkHoverColorRegex.exec(title)

      let styles = ''

      if (linkColor && linkColor[1]) {
        styles += `--color-link: ${linkColor[1]};`
      }

      if (badgeColor && badgeColor[1]) {
        styles += `--color-badge: ${badgeColor[1]};`
      }

      if (gap && gap[1]) {
        styles += `--gap: ${gap[1]}px;`
      }

      if (linkHoverColor && linkHoverColor[1]) {
        styles += `--color-link-hover: ${linkHoverColor[1]};`
      }

      return styles
    },

    /**
     * Get link font size.
     * @param {String} title - Title including link settings.
     * @returns {String|Null}
     */
    getLinkSizeClass(title) {
      const regex = new RegExp(/size:?(?<value>\w+)/, 'i')
      const sizeClass = regex.exec(title)

      return sizeClass && sizeClass[1]
        ? `navigation__link-title--${sizeClass[1].toLowerCase()}`
        : null
    },

    /**
     * Clear active links.
     */
    clearActiveLinks() {
      this.activeTier1 = false
      this.activeTier2 = false
    },

    /**
     * Handle link click for tier 2 urls.
     * @param {Object} link - Tier link object.
     */
    handleRedirect(link) {
      if (link.url) {
        this.redirecting = true

        if (link.tier === 1 && !this.isMobile) {
          localStorage.setItem('tier1Link', JSON.stringify(link))
        }

        localStorage.setItem('fgwp_canceled', 'true')
        window.location.href = link.url
      }
    },

    /**
     * Handle link click.
     * @param {Object} link - Tier link object.
     */
    handleLinkClick(link) {
      if (link.tier === 1) {
        this.activeTier2 = false

        if (this.activeTier1.handle === link.handle) {
          this.activeTier1 = false
          this.closeOverlay('navigation')
          return
        }

        this.activeTier1 = link

        this.openOverlay({
          ignoreDismissed: true,
          namespace: 'navigation',
        })

        klaviyoEvent({ label: link.title })

        return
      }

      if (this.activeTier2.handle === link.handle) {
        this.activeTier2 = false
        return
      }

      this.activeTier2 = link

      klaviyoEvent({
        label: this.activeTier1?.title ? `${this.activeTier1.title} > ${link.title}` : link.title,
      })
    },

    /**
     * Activate a link (and it's child menu) programmatically
     * @param link Tier link object
     */
    activateLink(link) {
      const delay = link.tier === 1 ? 150 : 100

      this.timeout = setTimeout(() => {
        if (link.tier === 2 && !link.items?.length) {
          this.activeTier2 = false
          return
        }

        this.handleLinkClick(link)
      }, delay)
    },

    /**
     * Handle link blur.
     * @param link Tier link object
     * @param tier1Index Tier1 index for the current link
     * @param tier2Index Tier2 index for the current link
     * @param tier3Index Tier3 index for the current link
     */
    handleLinkBlur(link, tier1Index, tier2Index, tier3Index) {
      // focus the first tier2 link when blurring a tier1 link with children
      if (link.tier === 1 && Array.isArray(this.$refs.tier2Links)) {
        const [firstLink] = this.$refs.tier2Links
        firstLink?.focus()

        return
      }

      // focus the next tier1 link when blurring the last tier2 link
      if (link.tier === 2 && tier2Index + 1 >= this.$refs.tier2Links?.length) {
        if (this.$refs.tier1Links[tier1Index + 1]) {
          this.$refs.tier1Links[tier1Index + 1]?.focus()
        } else {
          this.moveFocusOut()
        }

        return
      }

      // focus the next tier1 or 2 link when blurring the last tier3 link
      if (link.tier === 3 && tier3Index + 1 >= this.$refs.tier3Links?.length) {
        if (this.$refs.tier2Links[tier2Index + 1]) {
          this.$refs.tier2Links[tier2Index + 1].focus()
        } else if (this.$refs.tier1Links[tier1Index + 1]) {
          this.$refs.tier1Links[tier1Index + 1].focus()
        } else {
          this.moveFocusOut()
        }
      }
    },

    /**
     * Allow focus to move out of the nav
     */
    moveFocusOut() {
      this.focusLeaving = true
      this.closeOverlay('navigation')
    },

    /**
     * Open child menu on focusing a tier1 link
     * @param link Tier link object
     */
    handleLinkFocus(link) {
      // don't trigger menu if focus is leaving
      if (this.focusLeaving) {
        this.focusLeaving = !this.focusLeaving
      } else {
        this.closeBackdropOnHover = false

        if (link.tier === 1 && !this.getHasChildren(link)) {
          this.closeOverlay('navigation')
        } else {
          this.activateLink(link)
        }
      }
    },

    /**
     * Handle link hover.
     * @param {Object} link - Tier link object.
     */
    handleLinkHover(link) {
      this.closeBackdropOnHover = true

      if (link.tier === 1) {
        const tier1Link = JSON.parse(localStorage.getItem('tier1Link'))

        if ((!this.isMobile && tier1Link) &&
          (tier1Link.title.toLowerCase() === link.title.toLowerCase())) {
          return
        }

        localStorage.removeItem('tier1Link')
      }

      if (
        (link.tier === 1 && link.handle === this.activeTier1.handle) ||
        (link.tier === 2 && link.handle === this.activeTier2.handle)
      ) {
        return
      }

      this.activateLink(link)
    },

    /**
     * Handle link leave.
     */
    handleLinkLeave() {
      if (!this.redirecting && !this.isMobile) {
        localStorage.removeItem('tier1Link')
        clearTimeout(this.timeout)
      }
    },
  },
}
</script>
